import React, { useState } from 'react'
import {
  Calendar,
  ChevronDown,
  CreditCard,
  Edit2,
  Eye,
  TruckIcon,
} from 'lucide-react'
import { formatCurrency, formatDate } from '../../utils/formatters'
import './PurchaseTable.scss'

const PurchaseCard = ({ purchase, onView, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className='purchase-card'>
      <div className='card-header' onClick={() => setIsExpanded(!isExpanded)}>
        <div className='card-main-info'>
          <div className='client-info'>
            <div className='client-name'>
              {purchase.clientName.length > 20
                ? purchase.clientName.substring(0, 20) + '...'
                : purchase.clientName}
            </div>
            <div className='card-meta'>
              <Calendar size={14} />
              <span>{formatDate(purchase.date)}</span>
            </div>
          </div>

          <div className='card-amount'>
            <div className='amount-label'>Total</div>
            <div className='amount-value'>
              {formatCurrency(purchase.totalAmount)}
            </div>
          </div>
        </div>

        <div className='card-status'>
          <div>
            {purchase.remainingAmount > 0 && (
              <span
                style={{ marginRight: '10px' }}
                className='badge danger-outline'
              >
                Reste: {formatCurrency(purchase.remainingAmount)}
              </span>
            )}
            {purchase.deliveryStatus &&
              purchase.deliveryStatus !== 'default' && (
                <span
                  className={`badge ${
                    purchase.deliveryStatus === 'delivered'
                      ? 'success'
                      : 'warning'
                  }`}
                >
                  {purchase.deliveryStatus === 'delivered'
                    ? 'Livré'
                    : 'À livrer'}
                </span>
              )}
          </div>
          <ChevronDown
            size={20}
            className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
          />
        </div>
      </div>

      {isExpanded && (
        <div className='card-details'>
          <div className='details-section'>
            {purchase.clientPhone && (
              <div className='detail-row'>
                <span className='detail-label'>Téléphone</span>
                <span className='detail-value'>{purchase.clientPhone}</span>
              </div>
            )}

            <div className='detail-row'>
              <span className='detail-label'>
                <CreditCard size={14} />
                Paiement
              </span>
              <div className='detail-value'>
                {purchase.paymentMethods ? (
                  <div className='payment-badges-mobile'>
                    {purchase.paymentMethods.map((pm, index) => (
                      <span key={index} className='badge gray'>
                        {pm.method.replace('-', ' ')}:{' '}
                        {formatCurrency(pm.amount > 0 ? pm.amount : 0)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className='badge gray'>
                    {purchase.paymentMethod?.replace('-', ' ')}
                  </span>
                )}
              </div>
            </div>

            {/* <div className='detail-row'>
              <span className='detail-label'>Montant payé</span>
              <span className='detail-value font-medium'>
                {formatCurrency(
                  purchase.paymentMethods?.reduce(
                    (total, pm) => total + Number(pm.amount || 0),
                    0,
                  ) || purchase.totalAmount,
                )}
              </span>
            </div> */}

            {/* {purchase.remainingAmount > 0 && (
              <div className='detail-row highlight'>
                <span className='detail-label'>Reste à payer</span>
                <span className='detail-value text-danger'>
                  {formatCurrency(purchase.remainingAmount)}
                </span>
              </div>
            )} */}

            {purchase.deliveryStatus !== 'default' &&
              purchase.adressDelivery && (
                <div className='detail-row'>
                  <span className='detail-label'>
                    <TruckIcon size={14} />
                    Livraison
                  </span>
                  <span className='detail-value'>
                    {purchase.adressDelivery}
                  </span>
                </div>
              )}

            {purchase.observations && (
              <div className='detail-row full-width'>
                <span className='detail-label'>Observations</span>
                <span className='detail-value italic'>
                  "{purchase.observations}"
                </span>
              </div>
            )}
          </div>

          <div className='card-actions'>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onView(purchase)
              }}
              className='btn secondary sm'
            >
              <Eye size={16} className='btn-icon' />
              Détails
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEdit(purchase)
              }}
              className='btn primary sm'
            >
              <Edit2 size={16} className='btn-icon' />
              Modifier
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const PurchaseTable = ({ purchases, onView, onEdit }) => {
  return (
    <>
      {/* Version Cards pour mobile/tablette */}
      <div className='purchase-cards-container'>
        {purchases.map((purchase) => (
          <PurchaseCard
            key={purchase.id}
            purchase={purchase}
            onView={onView}
            onEdit={onEdit}
          />
        ))}
      </div>

      {/* Version Desktop */}
      <div className='overflow-x-auto'>
        <table className='table'>
          <thead>
            <tr>
              <th>Client</th>
              <th>Date</th>
              <th>Paiement</th>
              <th>Montant total</th>
              <th>Montant payé</th>
              <th>Reste à payer</th>
              <th>Livraison</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((purchase) => (
              <tr key={purchase.id}>
                <td>
                  <div className='cell-content'>
                    <div className='primary-text'>
                      {purchase.clientName.length > 15
                        ? purchase.clientName.substring(0, 15) + '...'
                        : purchase.clientName}
                    </div>
                    {purchase.clientPhone && (
                      <div className='secondary-text'>
                        {purchase.clientPhone}
                      </div>
                    )}
                    {purchase.observations && (
                      <div
                        className='secondary-text'
                        style={{
                          fontStyle: 'italic',
                          fontSize: '0.75rem',
                        }}
                      >
                        "
                        {purchase.observations.length > 20
                          ? purchase.observations.substring(0, 20) + '...'
                          : purchase.observations}
                        "
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <span className='text-sm'>{formatDate(purchase.date)}</span>
                </td>
                <td>
                  {purchase.paymentMethods ? (
                    <div className='payment-badges'>
                      {purchase.paymentMethods.map((pm, index) => (
                        <span key={index} className='badge gray'>
                          {pm.method.replace('-', ' ')}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className='badge gray'>
                      {purchase.paymentMethod?.replace('-', ' ')}
                    </span>
                  )}
                </td>
                <td>
                  <span className='font-bold'>
                    {formatCurrency(purchase.totalAmount)}
                  </span>
                </td>
                <td>
                  <span className='font-medium'>
                    {formatCurrency(
                      purchase.paymentMethods?.reduce(
                        (total, pm) => total + Number(pm.amount || 0),
                        0,
                      ) || purchase.totalAmount,
                    )}
                  </span>
                </td>
                <td>
                  <span
                    className={`font-medium ${
                      purchase.remainingAmount > 0
                        ? 'text-danger'
                        : 'text-success'
                    }`}
                  >
                    {formatCurrency(purchase.remainingAmount || 0)}
                  </span>
                </td>
                <td>
                  {(purchase.deliveryStatus || 'default') === 'default' ? (
                    <span> - </span>
                  ) : (
                    <span
                      className={`badge ${
                        (purchase.deliveryStatus || 'pending') === 'delivered'
                          ? 'success'
                          : 'warning'
                      }`}
                    >
                      {(purchase.deliveryStatus || 'pending') === 'delivered'
                        ? 'Livré'
                        : 'À livrer'}
                    </span>
                  )}
                </td>
                <td>
                  <div className='action-buttons'>
                    <button
                      style={{ width: '100%' }}
                      onClick={() => onView(purchase)}
                      className='btn secondary sm'
                    >
                      <Eye size={16} className='btn-icon' />
                      Détails
                    </button>
                    <button
                      style={{ width: '100%' }}
                      onClick={() => onEdit(purchase)}
                      className='btn primary sm'
                    >
                      <Edit2 size={16} className='btn-icon' />
                      Modifier
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export default PurchaseTable
